import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import '../../styles/barlow-condensed.scss';

import { buildVisualSlices, findVisualSliceIndex } from './spinWheelShared';
import type { SpinWheelVisualSegment } from './spinWheelVisual.types';
import { createSpinAnimation, playTickSound, playWinSound } from './wheelAnimation';
import { launchConfetti } from './wheelConfetti';
import { drawWheel } from './wheelRenderer';

export type SpinWheelCanvasHandle = {
  startPreSpin: () => void;
  finishSpin: (segmentId: string, isWin: boolean) => void;
  cancelSpin: () => void;
};

type SpinWheelCanvasProps = {
  segments: readonly SpinWheelVisualSegment[];
  size?: number;
  onSettle?: () => void;
  className?: string;
  idleSpin?: boolean;
  /** Blank themed fallback when public wheel artwork is unavailable. */
  decorative?: boolean;
};

const DPR = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;

const SpinWheelCanvas = forwardRef<SpinWheelCanvasHandle, SpinWheelCanvasProps>(
  ({ segments, size = 340, onSettle, className, idleSpin = false, decorative = false }, ref) => {
    const wheelCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const animRef = useRef<ReturnType<typeof createSpinAnimation> | null>(null);
    const confettiCleanupRef = useRef<(() => void) | null>(null);
    const settleCallbackRef = useRef(onSettle);
    const pendingWinRef = useRef(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
      const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      if (!preference) return;
      const update = () => setReducedMotion(preference.matches);
      update();
      preference.addEventListener('change', update);
      return () => preference.removeEventListener('change', update);
    }, []);

    useEffect(() => {
      settleCallbackRef.current = onSettle;
    }, [onSettle]);

    const visualSlices = useMemo(() => buildVisualSlices(segments), [segments]);

    const redraw = useCallback(
      (rotation: number) => {
        const canvas = wheelCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        drawWheel(ctx, visualSlices, size * DPR, rotation, decorative);
      },
      [visualSlices, size, decorative]
    );

    // Initial draw and redraw when segments change
    useEffect(() => {
      const currentRotation = animRef.current?.getState().rotation ?? 0;
      redraw(currentRotation);
    }, [redraw]);

    // Repaint stationary wheels once their self-hosted label font is ready.
    useEffect(() => {
      if (!document.fonts?.load) return;
      let active = true;
      void document.fonts.load('600 16px "Barlow Condensed"').then(() => {
        if (active) redraw(animRef.current?.getState().rotation ?? 0);
      }).catch(() => {
        // The condensed system fallback stays usable if the font cannot load.
      });
      return () => { active = false; };
    }, [redraw]);

    // Create animation controller
    useEffect(() => {
      const anim = createSpinAnimation({
        onFrame: (rotation) => redraw(rotation),
        onTick: () => playTickSound(),
        onSettle: () => {
          // Win effects fire only AFTER wheel fully stops
          if (pendingWinRef.current) {
            pendingWinRef.current = false;
            playWinSound();
            const confettiCanvas = confettiCanvasRef.current;
            if (confettiCanvas) {
              confettiCleanupRef.current?.();
              confettiCleanupRef.current = launchConfetti(
                confettiCanvas,
                visualSlices.map((s) => s.color)
              );
            }
          }
          settleCallbackRef.current?.();
        },
      });
      animRef.current = anim;
      return () => anim.destroy();
    }, [redraw, visualSlices]);

    // A fresh controller also needs its idle policy after artwork or size changes.
    useEffect(() => {
      animRef.current?.setIdleEnabled(idleSpin && !reducedMotion && (decorative || visualSlices.length > 0));
    }, [decorative, idleSpin, reducedMotion, redraw, visualSlices.length]);

    useImperativeHandle(ref, () => ({
      startPreSpin() {
        pendingWinRef.current = false;
        animRef.current?.startPreSpin();
      },

      finishSpin(segmentId: string, isWin: boolean) {
        // Her etkin backend segmenti artık yalnızca bir kez görselleştirilir.
        // Böylece sunucunun seçtiği id, 12 dilim sınırının ötesinde olsa bile
        // tek ve görünür bir hedefe iner.
        const targetIndex = findVisualSliceIndex(visualSlices, segmentId);
        if (targetIndex < 0) {
          animRef.current?.cancelSpin();
          settleCallbackRef.current?.();
          return;
        }

        // Store win state - confetti/sound will fire when wheel settles
        pendingWinRef.current = isWin;

        animRef.current?.finishSpin(targetIndex, visualSlices.length);
      },

      cancelSpin() {
        pendingWinRef.current = false;
        animRef.current?.cancelSpin();
      },
    }));

    // Cleanup confetti on unmount
    useEffect(() => {
      return () => {
        confettiCleanupRef.current?.();
      };
    }, []);

    const canvasPixels = size * DPR;

    return (
      <div className={className} style={{ position: 'relative', width: size, height: size }}>
        <canvas
          ref={wheelCanvasRef}
          width={canvasPixels}
          height={canvasPixels}
          style={{ width: size, height: size }}
          aria-hidden="true"
        />
        <canvas
          ref={confettiCanvasRef}
          width={canvasPixels}
          height={canvasPixels}
          style={{
            position: 'absolute',
            inset: 0,
            width: size,
            height: size,
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        />
      </div>
    );
  }
);

SpinWheelCanvas.displayName = 'SpinWheelCanvas';

export default SpinWheelCanvas;
